import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField, LoadingContent } from 'components'
import { getInspectionCompanyInformation, updateInspectionCompanyInformation } from 'controllers/companies/index'
import { CompanyDto } from 'dtos'
import { InspectionCompanyInformationDto } from 'dtos/companies'
import React, { useEffect, useState } from 'react'
import InspectionCompanyDialog from './InspectionCompanyDialog'
import { enqueueSnackbar } from 'notistack'

interface Props {
  company: CompanyDto
}

export default function InspectionCompanyCard({ company }: Props) {
  const [isGetting, setIsGetting] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<InspectionCompanyInformationDto>({ inspectionCompanyCode: undefined })
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!company.companyGuid) return
    setIsGetting(true)
    getInspectionCompanyInformation(company.companyGuid)
      .then(res => setInitialValues(res.value))
      .finally(() => setIsGetting(false))
  }, [company])

  return (
    <>
      <InspectionCompanyDialog
        initialValues={initialValues}
        isLoading={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          updateInspectionCompanyInformation(company.companyGuid!, values)
            .then(res => {
              enqueueSnackbar('Inspection Company Saved Successfully!', { variant: 'success' })
              setInitialValues(res.value)
              setDialogOpen(false)
            })
            .finally(() => setIsSaving(false))
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Inspection Company
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button disabled={isGetting} endIcon={<Edit />} onClick={() => setDialogOpen(true)} variant='text'>
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <LoadingContent isLoading={isGetting}>
              <Grid item xs={12}>
                <LabelField label='Inspection Company Code' value={initialValues.inspectionCompanyCode} />
              </Grid>
            </LoadingContent>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Inspection Company Card:**

This card is displayed only if the company is marked as an Inspection Company in the system.

**Edit Button:**

Clicking this button will open the Inspection Company dialog.

**Inspection Company Code:**

Displays the Inspection Company Code entered in the Inspection Company dialog.

If no value has been entered for the Inspection Company Code, a “-” will be displayed.

The Inspection Company Code is used on the Ohio Titles Out of State Motor Vehicle Physical Inspection form (BMV 4334) in the “Dealer Permit Number” field.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
